import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { PageHeader, useToast } from '../ui';
import { useLang } from '../Layout';
import { FaFileExcel, FaTableList, FaCircleCheck } from 'react-icons/fa6';

export default function Settings() {
  const { lang } = useLang();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const bn = lang === 'bn';

  async function downloadExcel() {
    setBusy(true);
    try {
      const { data } = await api.get('/backup');
      if (!data || !data.tables) throw new Error('কোনো ডেটা পাওয়া যায়নি');
      const { project, tables, exported_at } = data;

      const wb = XLSX.utils.book_new();
      const sheets = [
        ['মাল তালিকা', 'Materials', tables.materials, (r) => ({ 'নাম': r.name, 'একক': r.unit, 'ক্যাটাগরি': r.category, 'স্টক ইন': r.total_in ?? 0, 'স্টক আউট': r.total_out ?? 0, 'ব্যালেন্স': (r.total_in ?? 0) - (r.total_out ?? 0) })],
        ['স্টক ইন', 'Stock In', tables.stock_in, (r) => ({ 'তারিখ': r.created_at, 'মাল': r.material_name, 'পরিমাণ': r.qty, 'একক': r.unit, 'সাইড': r.contractor_name || 'কোম্পানি', 'সাপ্লায়ার': r.supplier, 'চালান': r.challan_no, 'মূল্য টাকা': r.amount, 'মন্তব্য': r.note, 'কে যোগ করেছে': r.created_by_name })],
        ['স্টক আউট', 'Stock Out', tables.stock_out, (r) => ({ 'তারিখ': r.created_at, 'মাল': r.material_name, 'পরিমাণ': r.qty, 'একক': r.unit, 'সাইড': r.contractor_name || '—', 'কর্মী': r.worker_name || '—', 'কাজের বিবরণ': r.work_description, 'মন্তব্য': r.note, 'কে দিয়েছে': r.created_by_name })],
        ['কন্ট্রাক্টর', 'Contractors', tables.contractors, (r) => ({ 'নাম': r.name, 'টাইপ': r.trade, 'ফোন': r.phone, 'সক্রিয়': r.is_active ? 'হ্যাঁ' : 'না' })],
        ['কর্মী', 'Workers', tables.workers, (r) => ({ 'নাম': r.name, 'কন্ট্রাক্টর': r.contractor_name, 'ফোন': r.phone, 'দৈনিক হার টাকা': r.rate_per_day, 'সক্রিয়': r.is_active ? 'হ্যাঁ' : 'না' })],
        ['হাজিরা', 'Attendance', tables.attendance, (r) => ({ 'তারিখ': r.date, 'কর্মী': r.worker_name, 'কন্ট্রাক্টর': r.contractor_name || '—', 'স্থিতি': r.status === 'present' ? 'উপস্থিত' : (r.status === 'absent' ? 'অনুপস্থিত' : 'ছুটি'), 'মন্তব্য': r.note })],
        ['খরচ', 'Expenses', tables.expenses, (r) => ({ 'তারিখ': r.date, 'বিবরণ': r.title, 'ধরন': r.category, 'পরিমাণ টাকা': r.amount, 'মন্তব্য': r.note, 'কে যোগ করেছে': r.created_by_name })],
        ['বেতন', 'Salaries', tables.salaries, (r) => ({ 'সপ্তাহ শুরু': r.week_start, 'কর্মী': r.worker_name, 'কন্ট্রাক্টর': r.contractor_name || '—', 'কাজের দিন': r.days_worked, 'মোট টাকা': r.raw_amount, 'নেট টাকা': r.net_amount, 'পরিশোধ': r.amount_paid, 'স্ট্যাটাস': r.status })],
        ['অগ্রিম', 'Advances', tables.advances, (r) => ({ 'তারিখ': r.date, 'কর্মী': r.worker_name, 'পরিমাণ টাকা': r.amount, 'মন্তব্য': r.note })],
        ['মাল অনুরোধ', 'Requests', tables.requests, (r) => ({ 'তারিখ': r.created_at, 'কন্ট্রাক্টর': r.contractor_name, 'মাল': r.material_name, 'পরিমাণ': r.qty, 'একক': r.unit, 'স্ট্যাটাস': r.status, 'মন্তব্য': r.note })],
      ];

      for (const [bnName, enName, rows, map] of sheets) {
        if (!rows || !rows.length) continue;
        const sheet = XLSX.utils.json_to_sheet((rows || []).map(map));
        XLSX.utils.book_append_sheet(wb, sheet, (bn ? bnName : enName).slice(0, 31));
      }

      const meta = XLSX.utils.aoa_to_sheet([
        ['প্রজেক্ট', project?.name || data.project?.name || ''],
        ['ঠিকানা', project?.address || ''],
        ['ব্যাকআপ তারিখ', exported_at],
      ]);
      XLSX.utils.book_append_sheet(wb, meta, 'info');

      XLSX.writeFile(wb, `Construction-Backup-${data.project?.name?.replace(/[^\w\u0980-\u09FF]+/g, '_') || 'project'}_${exported_at.slice(0, 10)}.xlsx`);
      toast(bn ? 'এক্সেল ব্যাকআপ ডাউনলোড হয়েছে' : 'Excel backup downloaded', 'success');
    } catch (e) {
      toast(e.message || 'ব্যান্ড আপ ব্যর্থ', 'error');
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <PageHeader title={bn ? 'সেটিংস' : 'Settings'} subtitle={bn ? 'ডেটা ব্যাকআপ ও প্রজেক্ট তথ্য' : 'Data backup & project info'} />

      <div className="grid md:grid-cols-2 gap-4">
        {/* Backup card */}
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg"><FaFileExcel /></span>
            <div>
              <h2 className="font-bold text-ink-900">{bn ? 'এক্সেল ব্যাকআপ' : 'Excel Backup'}</h2>
              <p className="text-xs text-ink-400">{bn ? 'সব ডেটা এক ফাইলে' : 'All data in one file'}</p>
            </div>
          </div>
          <p className="text-sm text-ink-500 leading-relaxed mb-4">
            {bn
              ? 'মাল তালিকা, স্টক ইন/আউট, কন্ট্রাক্টর, কর্মী, হাজিরা, খরচ, বেতন, অগ্রিম ও অনুরোধ — সব এক্সেল ফাইলে ডাউনলোড করুন। কোনো সময় ডেটা হারালে এই ফাইল থেকে পুনরুদ্ধার করতে পারবেন।'
              : 'Download materials, stock in/out, contractors, workers, attendance, expenses, salaries, advances & requests — all in one Excel file.'}
          </p>
          <button onClick={downloadExcel} disabled={busy}
            className={`w-full flex items-center justify-center gap-2 btn-primary py-3 ${busy ? 'opacity-60 cursor-wait' : ''}`}>
            <FaFileExcel /> {busy ? (bn ? 'তৈরি হচ্ছে…' : 'Preparing…') : (bn ? 'এক্সেল ডাউনলোড করুন' : 'Download Excel')}
          </button>
        </div>

        {/* What's included */}
        <div className="bg-white rounded-2xl border border-ink-100 shadow-soft p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center text-lg"><FaTableList /></span>
            <h2 className="font-bold text-ink-900">{bn ? 'যা যা আছে' : 'What is included'}</h2>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[bn ? 'মাল তালিকা' : 'Materials', bn ? 'স্টক ইন' : 'Stock In', bn ? 'স্টক আউট' : 'Stock Out', bn ? 'কন্ট্রাক্টর' : 'Contractors', bn ? 'কর্মী' : 'Workers', bn ? 'হাজিরা' : 'Attendance', bn ? 'খরচ' : 'Expenses', bn ? 'বেতন' : 'Salaries', bn ? 'অগ্রিম' : 'Advances', bn ? 'মাল অনুরোধ' : 'Requests'].map((s) => (
              <div key={s} className="flex items-center gap-2 text-ink-600 rounded-lg bg-ink-50/60 px-3 py-2"><FaCircleCheck className="text-emerald-500 shrink-0" size={14} />{s}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}